import React, { useEffect, useState } from "react";

import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";

import SearchField from "../../SearchField/SearchField";

interface FilterProps {
  data: string[];
  callbackSelected: (_selected: string[]) => void;
  initialSelected?: string[];
}

interface FilterItem {
  value: string;
  checked: boolean;
}

export const Filter: React.FC<FilterProps> = ({ data, callbackSelected, initialSelected = [] }) => {
  const [searchText, setSearchText] = useState<string>("");
  const [filterItems, setFilterItems] = useState<FilterItem[]>([]);

  // Initialize filter items when data changes
  useEffect(() => {
    const items = data.map((item) => ({
      value: item,
      checked: initialSelected.includes(item),
    }));
    setFilterItems(items);
    if (initialSelected.length === 0) {
      setSearchText("");
    }
  }, [data, initialSelected]);

  // Initial notification after items are set
  useEffect(() => {
    if (filterItems.length > 0) {
      const selectedItems = filterItems.filter((item) => item.checked).map((item) => item.value);
      if (selectedItems.length > 0) {
        callbackSelected(selectedItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterItems.length]); // Only run when items are first loaded

  // Filter items based on search text
  const filteredItems = filterItems.filter((item) =>
    item.value.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleChange = (text: string) => {
    setSearchText(text);
  };

  const handleItemChange = (value: string, checked: boolean) => {
    setFilterItems((prevItems) => {
      const newItems = prevItems.map((item) =>
        item.value === value ? { ...item, checked } : item
      );
      // Call callback with the new selection
      const selectedItems = newItems.filter((item) => item.checked).map((item) => item.value);
      callbackSelected(selectedItems);
      return newItems;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    const itemsToUpdate = searchText === "" ? filterItems : filteredItems;

    // Helper function to check if item should be updated
    const shouldUpdateItem = (item: FilterItem) =>
      itemsToUpdate.some((filteredItem) => filteredItem.value === item.value);

    setFilterItems((prevItems) => {
      const newItems = prevItems.map((item) => {
        return shouldUpdateItem(item) ? { ...item, checked } : item;
      });
      // Call callback with the new selection
      const selectedItems = newItems.filter((item) => item.checked).map((item) => item.value);
      callbackSelected(selectedItems);
      return newItems;
    });
  };

  // Check if all visible items are selected
  const allVisibleSelected =
    filteredItems.length > 0 && filteredItems.every((item) => item.checked);
  const someVisibleSelected = filteredItems.some((item) => item.checked);
  return (
    <div className="flex flow-column gap-10">
      <SearchField searchText={searchText} setSearchText={handleChange} placeholder="Search" />
      <div className="items_filter_list">
        <FormGroup>
          {filteredItems.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              }
              label="(Select All)"
            />
          )}
          {filteredItems.map((item: FilterItem) => (
            <FormControlLabel
              key={item.value}
              control={
                <Checkbox
                  checked={item.checked}
                  onChange={(e) => handleItemChange(item.value, e.target.checked)}
                />
              }
              label={item.value}
            />
          ))}
        </FormGroup>
      </div>
    </div>
  );
};
